import React from 'react';
import {
  Box, Chip, TextField, InputAdornment, IconButton, Tooltip,
  ToggleButtonGroup, ToggleButton, FormControl, Select, MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import CropPortraitIcon from '@mui/icons-material/CropPortrait';
import { IListningarCategory } from './types';
import { LISTNINGAR_CATEGORIES } from './categories';

interface ListningarFilterBarProps {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  categories?: IListningarCategory[];
  canManageCategories?: boolean;
  onManageCategories?: () => void;
  cardSize?: 'landscape' | 'square' | 'portrait';
  onCardSizeChange?: (size: 'landscape' | 'square' | 'portrait') => void;
  selectedWeek?: string;
  onWeekChange?: (week: string) => void;
  availableWeeks?: { value: string; label: string }[];
}

const ListningarFilterBar: React.FC<ListningarFilterBarProps> = ({
  selectedCategory,
  onCategoryChange,
  searchQuery,
  onSearchChange,
  categories,
  canManageCategories,
  onManageCategories,
  cardSize = 'landscape',
  onCardSizeChange,
  selectedWeek = 'all',
  onWeekChange,
  availableWeeks = [],
}) => {
  const cats = categories && categories.length > 0 ? categories : LISTNINGAR_CATEGORIES;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
        <Chip
          label="Alla"
          variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
          onClick={() => onCategoryChange('all')}
          sx={{
            fontWeight: selectedCategory === 'all' ? 700 : 400,
            bgcolor: selectedCategory === 'all' ? '#072f53' : 'transparent',
            color: selectedCategory === 'all' ? '#fff' : 'text.primary',
            borderColor: selectedCategory === 'all' ? '#072f53' : 'divider',
            '&:hover': {
              bgcolor: selectedCategory === 'all' ? '#072f53' : 'action.hover',
            },
          }}
        />
        {cats.map(cat => (
          <Chip
            key={cat.id}
            label={cat.label}
            variant={selectedCategory === cat.id ? 'filled' : 'outlined'}
            onClick={() => onCategoryChange(cat.id)}
            sx={{
              fontWeight: selectedCategory === cat.id ? 700 : 400,
              bgcolor: selectedCategory === cat.id ? cat.color : 'transparent',
              color: selectedCategory === cat.id ? '#fff' : 'text.primary',
              borderColor: selectedCategory === cat.id ? cat.color : 'divider',
              '&:hover': {
                bgcolor: selectedCategory === cat.id ? cat.color : `${cat.color}18`,
              },
            }}
          />
        ))}
        {canManageCategories && onManageCategories && (
          <Tooltip title="Hantera kategorier">
            <IconButton size="small" onClick={onManageCategories} sx={{ ml: 0.5 }}>
              <SettingsIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Sök bland inlägg..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: 180,
            maxWidth: 350,
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
          }}
        />

        {onWeekChange && availableWeeks.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={selectedWeek}
              onChange={(e) => onWeekChange(e.target.value)}
              displayEmpty
              sx={{ borderRadius: 2, fontSize: '0.85rem' }}
            >
              <MenuItem value="all">Alla veckor</MenuItem>
              {availableWeeks.map(w => (
                <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {onCardSizeChange && (
          <ToggleButtonGroup
            value={cardSize}
            exclusive
            onChange={(_, val) => val && onCardSizeChange(val)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 1,
                py: 0.5,
                borderColor: 'divider',
              },
            }}
          >
            <ToggleButton value="landscape">
              <Tooltip title="Liggande">
                <ViewStreamIcon sx={{ fontSize: 18 }} />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="square">
              <Tooltip title="Kvadratisk">
                <CropSquareIcon sx={{ fontSize: 18 }} />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="portrait">
              <Tooltip title="Stående">
                <CropPortraitIcon sx={{ fontSize: 18 }} />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>
    </Box>
  );
};

export default ListningarFilterBar;
